import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Select from "react-select";
import { Shimmer } from "Shimmers";
import { debounce } from "underscore";
import Switch from "Switch";
import BeatLoader from "react-spinners/BeatLoader";
import {
  usePaginatedCompanies,
  usePortfolioCompaniesTags,
  useTags,
} from "queryHooks";

import "./styles.scss";

const HeadlessCompanySearch = (props) => {
  const [isCompaniesLoading, setIsCompaniesLoading] = useState(true);

  const [renderTags, setRenderTags] = useState(props.defaultShowFilters);
  // const [isTagsLoading, setIsTagsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [preLoadedCompanies, setPreLoadedCompanies] = useState([]);

  const [selectedMarketTagIds, setSelectedMarketTagIds] = useState([]);
  const [selectedLocationTagIds, setSelectedLocationTagIds] = useState([]);
  const [selectedClassTagIds, setSelectedClassTagIds] = useState([]);
  const [
    selectedInvestmentRoundTagIds,
    setSelectedInvestmentRoundTagIds,
  ] = useState([]);

  const [currentPage, setCurrentPage] = useState(0);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(
    props.pageSize > -1 ? props.pageSize : 9
  );

  const [marketTagOptions, setMarketTagOptions] = useState([]);

  const [parentMarketTagOptions, setParentMarketTagOptions] = useState([]);
  const [childMarketTagOptions, setChildMarketTagOptions] = useState([]);

  const [
    childMarketTagOptionsToDisplay,
    setChildMarketTagOptionsToDisplay,
  ] = useState([]);

  const [classTagOptions, setClassTagOptions] = useState([]);
  const [countryTagOptions, setCountryTagOptions] = useState([]);
  const [stateTagOptions, setStateTagOptions] = useState([]);
  const [cityTagOptions, setCityTagOptions] = useState([]);

  const [investmentRoundTagOptions, setInvestmentRoundTagOptions] = useState(
    []
  );

  const [stateTagOptionsToDisplay, setStateTagOptionsToDisplay] = useState([]);
  const [cityTagOptionsToDisplay, setCityTagOptionsToDisplay] = useState([]);
  const [tagOptionsDisplayed, setTagOptionsDisplayed] = useState("market_tag");

  const [isMarketTagDrawerActive, toggleIsMarketTagDrawerActive] = useState(
    true
  );
  const [isLocationTagDrawerActive, toggleIsLocationTagDrawerActive] = useState(
    false
  );
  const [isClassTagDrawerActive, toggleIsClassTagDrawerActive] = useState(
    false
  );
  const [
    isInvestmentRoundTagDrawerActive,
    toggleIsInvestmentRoundTagDrawerActive,
  ] = useState(false);

  const visibility = props.companyVisibility || ["public"];

  const customReactSelectStyles = {
    control: (base, state) => ({
      ...base,
      height: "48px",
      width: "auto",
      borderColor: "#f4f4f4",
      WebkitBoxShadow: "1px 1px 1px 1px #f4f4f4",
      MozBoxShadow: "1px 1px 1px 1px #f4f4f4",
      boxShadow: "1px 1px 1px 1px #f4f4f4",

      "&:hover": {
        border: "1px solid #f4f4f4",
        cursor: "pointer",
        backgroundColor: "#f8f9fa",
      },
    }),
    input: (styles) => ({
      ...styles,

      alignItems: "center",
      display: "flex",

      "::before": {
        display: "block",
        marginRight: 8,
      },
    }),
    singleValue: (styles) => ({
      ...styles,

      paddingLeft: "65px",
    }),
    indicatorSeparator: (styles) => ({
      ...styles,

      backgroundColor: "white",
    }),
  };

  const sortByOptions = [
    {
      label: "Recently Updated",
      value: "last_published_post_timestamp__desc",
    },
    {
      label: "Recently Added",
      value: "timestamp_created__desc",
    },
  ];

  const [selectedSortOption, setSelectedSortOption] = useState(
    sortByOptions[0]
  );

  const generateNumberOfCompaniesText = () => {
    if (companiesResult.pages) {
      if (
        companiesResult.pages[0].pagination.count &&
        props.showRealCompanyTotalCount
      ) {
        return companiesResult.pages[0].pagination.count + " Companies";
      } else {
        if (companiesResult.pages[0].pagination.count > 500) {
          return "1000+ Companies";
        } else {
          return companiesResult.pages[0].pagination.count + " Companies";
        }
      }
    }
  };

  const handleClearPagingAndOffset = () => {
    setCurrentPage(0);
    setPreLoadedCompanies([]);
    setOffset(0);
  };

  const handleCompaniesLoading = () => {
    setIsCompaniesLoading(true);
  };

  //gets tags of all companies regardless of visibility
  const {
    data: portfolioCompaniesTags = [],
    portfolioCompaniesTagsStatus,
    portfolioCompaniesTagsError,
  } = usePortfolioCompaniesTags(props.portfolioId);

  const tagQuery = {
    isLinkedToCompany: true,
    includeParentChildTags: false,
    companyIds: props.baselineCompanyIds,
    visibility: props.companyVisibility,
    isEnabled: !props.portfolioId,
  };

  const {
    data: companiesTags = [],
    companiesTagsStatus,
    companiesTagsError,
  } = useTags(tagQuery);

  //filters the tags out into their respective client side click tracking arrays
  useEffect(() => {
    let fetchTagsLinkedToAnyCompany = async () => {
      let tagsLinkedToAnyCompany = [];

      if (companiesTags.length) {
        tagsLinkedToAnyCompany = companiesTags;
      } else if (portfolioCompaniesTags.length) {
        tagsLinkedToAnyCompany = portfolioCompaniesTags;
      }

      if (!tagsLinkedToAnyCompany.length) {
        return;
      }

      let countryTags = tagsLinkedToAnyCompany.filter(
        (tag) => tag.tag_type === "country_tag"
      );

      let stateTags = tagsLinkedToAnyCompany.filter(
        (tag) => tag.tag_type === "state_tag"
      );

      let cityTags = tagsLinkedToAnyCompany.filter(
        (tag) => tag.tag_type === "city_tag"
      );

      let marketTags = tagsLinkedToAnyCompany.filter(
        (tag) => tag.tag_type === "market_tag"
      );

      let parentMarketTags = marketTags.filter((marketTag) => {
        if (marketTag.parentTags.length === 0) {
          return true;
        }
      });

      let childMarketTags = marketTags.filter((marketTag) => {
        if (marketTag.parentTags.length > 0) {
          return true;
        }
      });

      let classTags = tagsLinkedToAnyCompany.filter((tag) => {
        if (tag.tag_type === "portfolio_tag") {
          if (!tag.meta_data || !tag.meta_data.isDiscoverable) {
            return false;
          }
          return true;
        }
      });

      let investmentRoundTags = tagsLinkedToAnyCompany.filter(
        (tag) => tag.tag_type === "investment_round_tag"
      );

      setMarketTagOptions(marketTags);

      setParentMarketTagOptions(parentMarketTags);
      setChildMarketTagOptions(childMarketTags);

      setClassTagOptions(classTags);
      setCountryTagOptions(countryTags);
      setStateTagOptions(stateTags);
      setCityTagOptions(cityTags);
      setInvestmentRoundTagOptions(investmentRoundTags);
    };

    fetchTagsLinkedToAnyCompany();
  }, [portfolioCompaniesTags.length, companiesTags.length]);

  // removes the active parent tags from the selected list if at least one of their children
  // are selected. This allows us to emulate an AND while drilling down on location.
  const getReducedParentLocationTags = () => {
    let countryTagsToOmit = [];
    let stateTagsToOmit = [];

    if (stateTagOptions) {
      let selectedStateTagOptions = stateTagOptions.filter((stateTagOption) => {
        if (stateTagOption.active) {
          if (stateTagOption.parentTags.length) {
            // console.log(stateTagOption.parentTags);
            countryTagsToOmit = countryTagsToOmit.concat(
              stateTagOption.parentTags
            );
          }

          return true;
        }
      });
    }

    if (cityTagOptions) {
      let selectedCityTagOptions = cityTagOptions.filter((cityTagOption) => {
        if (cityTagOption.active) {
          if (cityTagOption.parentTags.length) {
            stateTagsToOmit = stateTagsToOmit.concat(cityTagOption.parentTags);
          }

          return true;
        }
      });
    }

    let totalTagsToOmit = stateTagsToOmit.concat(countryTagsToOmit);

    let finalSelectedLocationTagIds = [];

    //remove parent location tags if their children are selected
    for (let i in selectedLocationTagIds) {
      let shouldRemoveTag = false;
      for (let j in totalTagsToOmit) {
        if (totalTagsToOmit[j].tag_id === selectedLocationTagIds[i]) {
          shouldRemoveTag = true;
          continue;
        }
      }

      if (!shouldRemoveTag) {
        finalSelectedLocationTagIds.push(selectedLocationTagIds[i]);
      }
    }

    return finalSelectedLocationTagIds;
  };

  const getReducedParentMarketTags = () => {
    let parentMarketTagsToOmit = [];

    if (parentMarketTagOptions) {
      let selectedChildTagOptions = childMarketTagOptions.filter(
        (childMarketTagOption) => {
          if (childMarketTagOption.active) {
            if (childMarketTagOption.parentTags.length) {
              parentMarketTagsToOmit = parentMarketTagsToOmit.concat(
                childMarketTagOption.parentTags
              );
            }

            return true;
          }
        }
      );
    }

    let totalTagsToOmit = parentMarketTagsToOmit;

    let finalSelectedMarketTagIds = [];

    //remove parent location tags if their children are selected
    for (let i in selectedMarketTagIds) {
      let shouldRemoveTag = false;
      for (let j in totalTagsToOmit) {
        if (totalTagsToOmit[j].tag_id === selectedMarketTagIds[i]) {
          shouldRemoveTag = true;
          continue;
        }
      }

      if (!shouldRemoveTag) {
        finalSelectedMarketTagIds.push(selectedMarketTagIds[i]);
      }
    }

    return finalSelectedMarketTagIds;
  };

  const handleFilterItemClick = (tagOptionsToDisplay) => {
    if (tagOptionsToDisplay === "market_tag") {
      toggleIsMarketTagDrawerActive(!isMarketTagDrawerActive);
      toggleIsClassTagDrawerActive(false);
      toggleIsLocationTagDrawerActive(false);
      toggleIsInvestmentRoundTagDrawerActive(false);
    }

    if (tagOptionsToDisplay === "portfolio_tag") {
      toggleIsMarketTagDrawerActive(false);
      toggleIsClassTagDrawerActive(!isClassTagDrawerActive);
      toggleIsLocationTagDrawerActive(false);
      toggleIsInvestmentRoundTagDrawerActive(false);
    }

    if (tagOptionsToDisplay === "location_tag") {
      toggleIsMarketTagDrawerActive(false);
      toggleIsClassTagDrawerActive(false);
      toggleIsLocationTagDrawerActive(!isLocationTagDrawerActive);
      toggleIsInvestmentRoundTagDrawerActive(false);
    }

    if (tagOptionsToDisplay === "investment_round_tag") {
      toggleIsMarketTagDrawerActive(false);
      toggleIsClassTagDrawerActive(false);
      toggleIsLocationTagDrawerActive(false);
      toggleIsInvestmentRoundTagDrawerActive(!isInvestmentRoundTagDrawerActive);
    }

    if (tagOptionsToDisplay === tagOptionsDisplayed) {
      setTagOptionsDisplayed("");
    } else {
      setTagOptionsDisplayed(tagOptionsToDisplay);
    }
  };

  const handleParentMarketTagClick = (tag) => {
    handleClearPagingAndOffset();
    handleCompaniesLoading();

    let hasBeenToggledActive = true;
    let tagClicked = {};

    // mark active or inactive
    let newParentMarketTagOptions = parentMarketTagOptions.map(
      (parentMarketTagOption) => {
        if (parentMarketTagOption.tag_id === tag.tag_id) {
          parentMarketTagOption.active = !parentMarketTagOption.active;
          tagClicked = parentMarketTagOption;

          if (parentMarketTagOption.active) {
            selectedMarketTagIds.push(tag.tag_id);
          } else {
            hasBeenToggledActive = false;
            let index = selectedMarketTagIds.indexOf(tag.tag_id);
            if (index > -1) {
              selectedMarketTagIds.splice(index, 1);
            }
          }
        }

        return parentMarketTagOption;
      }
    );

    let newChildMarketTagOptions = childMarketTagOptions;

    //handle toggling child market tags inactive if parent market tag is toggled inactive
    if (!hasBeenToggledActive) {
      newChildMarketTagOptions = childMarketTagOptions.map(
        (childMarketTagOption) => {
          for (let i in childMarketTagOption.parentTags) {
            if (
              childMarketTagOption.parentTags[i].tag_id === tagClicked.tag_id
            ) {
              childMarketTagOption.active = false;

              let index = selectedMarketTagIds.indexOf(
                childMarketTagOption.tag_id
              );
              if (index > -1) {
                selectedMarketTagIds.splice(index, 1);
              }
              continue;
            }
          }
          return childMarketTagOption;
        }
      );
    }

    setSelectedMarketTagIds(selectedMarketTagIds);
    setChildMarketTagOptions(newChildMarketTagOptions);
    setParentMarketTagOptions(newParentMarketTagOptions);

    let activeParentMarketTags = newParentMarketTagOptions.filter(
      (parentMarketTagOption) => {
        if (parentMarketTagOption.active) {
          return parentMarketTagOption;
        }
      }
    );

    let childMarketTagOptionsToDisplay = [];

    // identify and set the child tags to display based on parent tag selection
    for (let i in activeParentMarketTags) {
      for (let k in childMarketTagOptions) {
        let childTags = activeParentMarketTags[i].childTags;

        for (let j in childTags) {
          if (childTags[j].tag_id === childMarketTagOptions[k].tag_id) {
            childMarketTagOptionsToDisplay.push(childMarketTagOptions[k]);
          }
        }
      }
    }

    setChildMarketTagOptionsToDisplay(childMarketTagOptionsToDisplay);
  };

  const handleChildMarketTagClick = (tag) => {
    handleClearPagingAndOffset();
    handleCompaniesLoading();

    // set active/inactive and push or remove from selected list
    let newChildMarketTagOptions = childMarketTagOptions.map(
      (childMarketTagOption) => {
        if (childMarketTagOption.tag_id === tag.tag_id) {
          childMarketTagOption.active = !childMarketTagOption.active;

          if (childMarketTagOption.active) {
            selectedMarketTagIds.push(tag.tag_id);
          } else {
            let index = selectedMarketTagIds.indexOf(tag.tag_id);
            if (index > -1) {
              selectedMarketTagIds.splice(index, 1);
            }
          }
        }

        return childMarketTagOption;
      }
    );

    setSelectedMarketTagIds(selectedMarketTagIds);
    setChildMarketTagOptions(newChildMarketTagOptions);
  };

  const handleClassTagClick = (tag) => {
    handleClearPagingAndOffset();
    handleCompaniesLoading();
    let newClassTagOptions = classTagOptions.map((classTagOption) => {
      if (classTagOption.tag_id === tag.tag_id) {
        classTagOption.active = !classTagOption.active;

        if (classTagOption.active) {
          selectedClassTagIds.push(tag.tag_id);
        } else {
          let index = selectedClassTagIds.indexOf(tag.tag_id);
          if (index > -1) {
            selectedClassTagIds.splice(index, 1);
          }
        }
      }

      return classTagOption;
    });

    setSelectedClassTagIds(selectedClassTagIds);

    setClassTagOptions(newClassTagOptions);
  };

  const handleInvestmentRoundTagClick = (tag) => {
    handleClearPagingAndOffset();
    handleCompaniesLoading();

    let newInvestmentRoundTagOptions = investmentRoundTagOptions.map(
      (investmentRoundTagOption) => {
        if (investmentRoundTagOption.tag_id === tag.tag_id) {
          investmentRoundTagOption.active = !investmentRoundTagOption.active;

          if (investmentRoundTagOption.active) {
            selectedInvestmentRoundTagIds.push(tag.tag_id);
          } else {
            let index = selectedInvestmentRoundTagIds.indexOf(tag.tag_id);
            if (index > -1) {
              selectedInvestmentRoundTagIds.splice(index, 1);
            }
          }
        }

        return investmentRoundTagOption;
      }
    );

    setSelectedInvestmentRoundTagIds(selectedInvestmentRoundTagIds);
    setInvestmentRoundTagOptions(newInvestmentRoundTagOptions);
  };

  const handleCountryTagClick = (tag) => {
    handleClearPagingAndOffset();
    handleCompaniesLoading();
    let hasBeenToggledActive = true;
    let tagClicked = {};

    // handles toggling active/inactive and removes from selectedLocationTagIds list
    let newCountryTagOptions = countryTagOptions.map((countryTagOption) => {
      if (countryTagOption.tag_id === tag.tag_id) {
        tagClicked = countryTagOption;

        countryTagOption.active = !countryTagOption.active;
        if (countryTagOption.active) {
          selectedLocationTagIds.push(tag.tag_id);
        } else {
          hasBeenToggledActive = false;
          let index = selectedLocationTagIds.indexOf(tag.tag_id);
          if (index > -1) {
            selectedLocationTagIds.splice(index, 1);
          }
        }
      }

      return countryTagOption;
    });

    let newStateTagOptions = stateTagOptions;
    let newCityTagOptions = cityTagOptions;

    //handle toggling states and citys inactive if country is toggled
    if (!hasBeenToggledActive) {
      newStateTagOptions = stateTagOptions.map((stateTagOption) => {
        for (let i in stateTagOption.parentTags) {
          if (stateTagOption.parentTags[i].tag_id === tagClicked.tag_id) {
            stateTagOption.active = false;

            let index = selectedLocationTagIds.indexOf(stateTagOption.tag_id);
            if (index > -1) {
              selectedLocationTagIds.splice(index, 1);
            }
            continue;
          }
        }
        return stateTagOption;
      });

      newCityTagOptions = cityTagOptions.map((cityTagOption) => {
        for (let i in cityTagOption.parentTags) {
          if (cityTagOption.parentTags[i].tag_id === tagClicked.tag_id) {
            cityTagOption.active = false;

            let index = selectedLocationTagIds.indexOf(cityTagOption.tag_id);
            if (index > -1) {
              selectedLocationTagIds.splice(index, 1);
            }
            continue;
          }
        }
        return cityTagOption;
      });
    }

    setStateTagOptions(newStateTagOptions);
    setCityTagOptions(newCityTagOptions);

    setSelectedLocationTagIds(selectedLocationTagIds);
    setCountryTagOptions(newCountryTagOptions);

    let activeCountryTags = newCountryTagOptions.filter((countryTagOption) => {
      if (countryTagOption.active) {
        return countryTagOption;
      }
    });

    let stateTagOptionsToDisplay = [];

    for (let i in activeCountryTags) {
      for (let k in stateTagOptions) {
        let childTags = activeCountryTags[i].childTags;

        for (let j in childTags) {
          if (childTags[j].tag_id === stateTagOptions[k].tag_id) {
            stateTagOptionsToDisplay.push(stateTagOptions[k]);
          }
        }
      }
    }

    setStateTagOptionsToDisplay(stateTagOptionsToDisplay);
  };

  const handleStateTagClick = (tag) => {
    handleClearPagingAndOffset();
    handleCompaniesLoading();

    let hasBeenToggledActive = true;
    let tagClicked = {};

    let newStateTagOptions = stateTagOptions.map((stateTagOption) => {
      if (stateTagOption.tag_id === tag.tag_id) {
        tagClicked = stateTagOption;

        stateTagOption.active = !stateTagOption.active;

        if (stateTagOption.active) {
          selectedLocationTagIds.push(tag.tag_id);
        } else {
          hasBeenToggledActive = false;
          let index = selectedLocationTagIds.indexOf(tag.tag_id);
          if (index > -1) {
            selectedLocationTagIds.splice(index, 1);
          }
        }
      }

      return stateTagOption;
    });

    let newCityTagOptions = cityTagOptions;

    if (!hasBeenToggledActive) {
      newCityTagOptions = cityTagOptions.map((cityTagOption) => {
        for (let i in cityTagOption.parentTags) {
          if (cityTagOption.parentTags[i].tag_id === tagClicked.tag_id) {
            cityTagOption.active = false;

            let index = selectedLocationTagIds.indexOf(cityTagOption.tag_id);
            if (index > -1) {
              selectedLocationTagIds.splice(index, 1);
            }
            continue;
          }
        }
        return cityTagOption;
      });
    }

    setCityTagOptions(newCityTagOptions);
    setSelectedLocationTagIds(selectedLocationTagIds);
    setStateTagOptions(newStateTagOptions);

    let selectedStateTags = newStateTagOptions.filter((stateTagOption) => {
      if (stateTagOption.active) {
        return stateTagOption;
      }
    });

    let cityTagOptionsToDisplay = [];

    for (let i in selectedStateTags) {
      for (let k in cityTagOptions) {
        let curStateChildTags = selectedStateTags[i].childTags;

        for (let j in curStateChildTags) {
          if (curStateChildTags[j].tag_id === cityTagOptions[k].tag_id) {
            cityTagOptionsToDisplay.push(cityTagOptions[k]);
          }
        }
      }
    }

    setCityTagOptionsToDisplay(cityTagOptionsToDisplay);
  };

  const handleCityTagClick = (tag) => {
    handleClearPagingAndOffset();
    handleCompaniesLoading();

    let newCityTagOptions = cityTagOptions.map((cityTagOption) => {
      if (cityTagOption.tag_id === tag.tag_id) {
        cityTagOption.active = !cityTagOption.active;

        if (cityTagOption.active) {
          selectedLocationTagIds.push(tag.tag_id);
        } else {
          let index = selectedLocationTagIds.indexOf(tag.tag_id);
          if (index > -1) {
            selectedLocationTagIds.splice(index, 1);
          }
        }
      }

      return cityTagOption;
    });

    setSelectedLocationTagIds(selectedLocationTagIds);

    setCityTagOptions(newCityTagOptions);
  };

  const handleClearFilters = () => {
    handleClearPagingAndOffset();
    if (
      selectedMarketTagIds.length ||
      selectedLocationTagIds.length ||
      selectedClassTagIds.length ||
      selectedInvestmentRoundTagIds.length ||
      searchQuery.length
    ) {
      handleCompaniesLoading();
    }

    let inactiveClassTagOptions = classTagOptions.map((classTagOption) => {
      classTagOption.active = false;
      return classTagOption;
    });

    let inactiveMarketTagOptions = marketTagOptions.map((marketTagOption) => {
      marketTagOption.active = false;
      return marketTagOption;
    });

    let inactiveCountryTagOptions = countryTagOptions.map(
      (countryTagOption) => {
        countryTagOption.active = false;
        return countryTagOption;
      }
    );

    let inactiveStateTagOptions = stateTagOptions.map((stateTagOption) => {
      stateTagOption.active = false;
      return stateTagOption;
    });

    let inactiveCityTagOptions = cityTagOptions.map((cityTagOption) => {
      cityTagOption.active = false;
      return cityTagOption;
    });

    let inactiveInvestmentRoundTagOptions = investmentRoundTagOptions.map(
      (investmentRoundTagOption) => {
        investmentRoundTagOption.active = false;
        return investmentRoundTagOption;
      }
    );

    setSelectedMarketTagIds([]);
    setSelectedLocationTagIds([]);
    setSelectedClassTagIds([]);
    setSelectedInvestmentRoundTagIds([]);

    setClassTagOptions(inactiveClassTagOptions);
    setMarketTagOptions(inactiveMarketTagOptions);
    setCountryTagOptions(inactiveCountryTagOptions);
    setStateTagOptions(inactiveStateTagOptions);
    setCityTagOptions(inactiveCityTagOptions);
    setInvestmentRoundTagOptions(inactiveInvestmentRoundTagOptions);

    setStateTagOptionsToDisplay([]);
    setCityTagOptionsToDisplay([]);

    document.querySelector("#company-search-query").value = "";
    setSearchQuery("");
  };

  const debouncedHandleSearchQuery = debounce((value) => {
    handleCompaniesLoading();
    handleClearPagingAndOffset();
    setSearchQuery(value);
  }, 1000);

  const parentMarketTagsHtml = parentMarketTagOptions.map((tag, index) => {
    if (tag.parentTags.length === 0) {
      return (
        <div
          key={index}
          className={`ps-company-search__tag ${
            tag.active ? "ps-company-search__tag--active" : ""
          }`}
          tagid={tag.tag_id}
          onClick={() => handleParentMarketTagClick(tag)}
        >
          <div>{tag.display_name}</div>
        </div>
      );
    }
  });

  const childMarketTagsHtml = childMarketTagOptionsToDisplay.map(
    (tag, index) => {
      return (
        <div
          key={index}
          className={`ps-company-search__tag ${
            tag.active ? "ps-company-search__tag--active" : ""
          }`}
          tagid={tag.tag_id}
          onClick={() => handleChildMarketTagClick(tag)}
        >
          <div>{tag.display_name}</div>
        </div>
      );
    }
  );

  const classTagsHtml = classTagOptions.map((tag, index) => (
    <div
      key={index}
      className={`ps-company-search__tag ${
        tag.active ? "ps-company-search__tag--active" : ""
      }`}
      tagid={tag.tag_id}
      onClick={() => handleClassTagClick(tag)}
    >
      <div>{tag.display_name}</div>
    </div>
  ));

  const investmentRoundTagsHtml = investmentRoundTagOptions.map(
    (tag, index) => (
      <div
        key={index}
        className={`ps-company-search__tag ${
          tag.active ? "ps-company-search__tag--active" : ""
        }`}
        tagid={tag.tag_id}
        onClick={() => handleInvestmentRoundTagClick(tag)}
      >
        <div>{tag.display_name}</div>
      </div>
    )
  );

  const countryTagsHtml = countryTagOptions.map((tag, index) => (
    <div
      key={index}
      className={`ps-company-search__tag ${
        tag.active ? "ps-company-search__tag--active" : ""
      }`}
      tagid={tag.tag_id}
      onClick={() => handleCountryTagClick(tag)}
    >
      <div>{tag.display_name}</div>
    </div>
  ));

  const stateTagsHtml = stateTagOptionsToDisplay.map((tag, index) => (
    <div
      key={index}
      className={`ps-company-search__tag ${
        tag.active ? "ps-company-search__tag--active" : ""
      }`}
      tagid={tag.tag_id}
      onClick={() => handleStateTagClick(tag)}
    >
      <div>{tag.display_name}</div>
    </div>
  ));

  const cityTagsHtml = cityTagOptionsToDisplay.map((tag, index) => (
    <div
      key={index}
      className={`ps-company-search__tag ${
        tag.active ? "ps-company-search__tag--active" : ""
      }`}
      tagid={tag.tag_id}
      onClick={() => handleCityTagClick(tag)}
    >
      <div>{tag.display_name}</div>
    </div>
  ));

  let children = () => {
    if (isFetchingNextPage) {
      return (
        <div>
          {props.generateChildren(
            companiesResult.pages.map((page) => page.data).flat()
          )}

          <div className="ps-company-search__load-more-container">
            <button
              className="btn ps-btn--white"
              disabled={!hasNextPage || isFetchingNextPage}
            >
              <BeatLoader
                size={5}
                color={"#000000"}
                loading={true}
              ></BeatLoader>
            </button>
          </div>

          {/* {props.generateLoading()} */}
        </div>
      );
    } else if (companiesResult && companiesResult.pages) {
      return props.generateChildren(
        companiesResult.pages.map((page) => page.data).flat()
      );
    } else {
      return props.generateLoading();
    }
  };

  let companyIds = "";
  if (
    !(props.baselineCompanyIds === "" || props.baselineCompanyIds === undefined)
  ) {
    companyIds = props.baselineCompanyIds.join(",");
  }

  let reducedSelectedMarketTags = getReducedParentMarketTags();
  let commaSeparatedMarketTagString = reducedSelectedMarketTags.join();
  let commaSeparatedClassTagString = selectedClassTagIds.join();

  //reduce the location tags by removing any parents from the query
  let reducedSelectedLocationTags = getReducedParentLocationTags();
  let commaSeparatedLocationTagString = reducedSelectedLocationTags.join();

  let commaSeparatedInvestmentRoundTags = selectedInvestmentRoundTagIds.join();

  const {
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status: companiesRequestStatus,
    error: companiesError,
    data: companiesResult = [],
  } = usePaginatedCompanies({
    searchQuery: searchQuery,
    offset: offset,
    limit: pageSize,
    companyIds: props.baselineCompanyIds,
    baselinePortfolioTagIds: props.baselinePortfolioTagIds,
    marketTagIds: reducedSelectedMarketTags,
    locationTagIds: reducedSelectedLocationTags,
    portfolioTagIds: selectedClassTagIds,
    investmentRoundTagIds: selectedInvestmentRoundTagIds,
    visibility: visibility,
    sortBy: selectedSortOption.value.split("__")[0],
    orderBy: selectedSortOption.value.split("__")[1],
    apiSlug: props.apiSlug,
  });

  // console.log("companiesResult");
  // console.log(companiesResult);

  return (
    <div>
      <div className="ps-company-search__header">
        <div className="ps-company-search__company-count-container">
          <div className="ps-heading--vline">
            <h3 className="ps-heading--vline__text">
              {!companiesResult.pages && (
                <React.Fragment>
                  <Shimmer
                    shimmerStyles={{
                      width: "140px",
                      height: "22px",
                      borderRadius: "4px",
                    }}
                  />
                </React.Fragment>
              )}
              {companiesResult && companiesResult.pages && (
                <React.Fragment>
                  {generateNumberOfCompaniesText()}
                </React.Fragment>
              )}
            </h3>
          </div>
        </div>
        <div className="ps-company-search__sort-by-select-switch">
          {props.renderSwitch && (
            <Switch
              labelPosition={"left"}
              label={"Show Filters"}
              checked={renderTags}
              onChange={(event) => {
                handleClearFilters();
                setRenderTags(event.target.checked);
              }}
            />
          )}

          <div className="ps-company-search__query-input-container">
            <input
              placeholder="Search Companies"
              type="text"
              name="query"
              id="company-search-query"
              autoComplete="off"
              // onKeyDown={(e) => {
              //   if (e.key === "Enter") {
              //     console.log("searchQuery");
              //     console.log(searchQuery);

              //     console.log("e.target.value");
              //     console.log(e.target.value);
              //     if (e.target.value !== searchQuery) {
              //       console.log("calling")
              //       handleCompaniesLoading();
              //       handleClearPagingAndOffset();
              //       setSearchQuery(e.target.value);
              //     }
              //   }
              // }}
              onChange={(e) => {
                debouncedHandleSearchQuery(e.target.value);
              }}
              className="input-text--primary ps-company-search__query-input"
            />
            <div className="ps-company-search__query-input-x-button">
              <img
                onClick={() => {
                  if (
                    document.querySelector("#company-search-query").value !== ""
                  ) {
                    document.querySelector("#company-search-query").value = "";
                    handleCompaniesLoading();
                    handleClearPagingAndOffset();
                    setSearchQuery("");
                  }
                }}
                src="/images/icons/gray-x-mark.svg"
              />
            </div>
          </div>
        </div>
      </div>
      {renderTags && (
        <div>
          <div className="ps-company-search__filter-sort-bar-container">
            <div className="ps-company-search__filter-bar">
              {props.showMarketTags && (
                <div
                  className="ps-company-search__filter-bar-item ps-company-search__filter-bar-item--flex-2"
                  onClick={() => handleFilterItemClick("market_tag")}
                >
                  <div
                    className={`ps-company-search__filter-bar-item-text ${
                      isMarketTagDrawerActive
                        ? "ps-company-search__filter-bar-item-text--active"
                        : ""
                    }`}
                  >
                    Market
                  </div>
                  <div className="ps-company-search__filter-bar-badge-arrow-container">
                    <div className="ps-company-search__filter-bar-item-select-number-badge-container">
                      {selectedMarketTagIds.length > 0 && (
                        <div className="ps-company-search__filter-bar-item-select-number-badge">
                          {selectedMarketTagIds.length}
                        </div>
                      )}
                    </div>

                    <div
                      className={`ps-company-search__filter-bar-item-dropdown-arrow ${
                        isMarketTagDrawerActive
                          ? ""
                          : "ps-company-search__filter-bar-item-dropdown-arrow--inactive"
                      }`}
                    >
                      <img src="/images/icons/dark-gray-dropdown-arrow.svg" />
                    </div>
                  </div>
                </div>
              )}
              {props.showLocationTags && (
                <div
                  className="ps-company-search__filter-bar-item ps-company-search__filter-bar-item--flex-2"
                  onClick={() => handleFilterItemClick("location_tag")}
                >
                  <div
                    className={`ps-company-search__filter-bar-item-text ${
                      isLocationTagDrawerActive
                        ? "ps-company-search__filter-bar-item-text--active"
                        : ""
                    }`}
                  >
                    Location
                  </div>
                  <div className="ps-company-search__filter-bar-item-select-number-badge-container">
                    {selectedLocationTagIds.length > 0 && (
                      <div className="ps-company-search__filter-bar-item-select-number-badge">
                        {selectedLocationTagIds.length}
                      </div>
                    )}
                  </div>
                  <div
                    className={`ps-company-search__filter-bar-item-dropdown-arrow ${
                      isLocationTagDrawerActive
                        ? ""
                        : "ps-company-search__filter-bar-item-dropdown-arrow--inactive"
                    }`}
                  >
                    <img src="/images/icons/dark-gray-dropdown-arrow.svg" />
                  </div>
                </div>
              )}
              {props.showPortfolioTags && (
                <div
                  className="ps-company-search__filter-bar-item ps-company-search__filter-bar-item--flex-2"
                  onClick={() => handleFilterItemClick("portfolio_tag")}
                >
                  <div
                    className={`ps-company-search__filter-bar-item-text ${
                      isClassTagDrawerActive
                        ? "ps-company-search__filter-bar-item-text--active"
                        : ""
                    }`}
                  >
                    Portfolio
                  </div>
                  <div className="ps-company-search__filter-bar-item-select-number-badge-container">
                    {selectedClassTagIds.length > 0 && (
                      <div className="ps-company-search__filter-bar-item-select-number-badge">
                        {selectedClassTagIds.length}
                      </div>
                    )}
                  </div>
                  <div
                    className={`ps-company-search__filter-bar-item-dropdown-arrow ${
                      isClassTagDrawerActive
                        ? ""
                        : "ps-company-search__filter-bar-item-dropdown-arrow--inactive"
                    }`}
                  >
                    <img src="/images/icons/dark-gray-dropdown-arrow.svg" />
                  </div>
                </div>
              )}
              {props.showInvestmentRoundTags && (
                <div
                  className="ps-company-search__filter-bar-item ps-company-search__filter-bar-item--flex-2"
                  onClick={() => handleFilterItemClick("investment_round_tag")}
                >
                  <div
                    className={`ps-company-search__filter-bar-item-text ${
                      isInvestmentRoundTagDrawerActive
                        ? "ps-company-search__filter-bar-item-text--active"
                        : ""
                    }`}
                  >
                    Stage
                  </div>
                  <div className="ps-company-search__filter-bar-item-select-number-badge-container">
                    {selectedInvestmentRoundTagIds.length > 0 && (
                      <div className="ps-company-search__filter-bar-item-select-number-badge">
                        {selectedInvestmentRoundTagIds.length}
                      </div>
                    )}
                  </div>
                  <div
                    className={`ps-company-search__filter-bar-item-dropdown-arrow ${
                      isInvestmentRoundTagDrawerActive
                        ? ""
                        : "ps-company-search__filter-bar-item-dropdown-arrow--inactive"
                    }`}
                  >
                    <img src="/images/icons/dark-gray-dropdown-arrow.svg" />
                  </div>
                </div>
              )}
              <div
                className="ps-company-search__filter-bar-item ps-company-search__clear-filters"
                onClick={() => handleClearFilters()}
              >
                Clear Filters
              </div>
            </div>
            <div className="ps-company-search__sort-by-select-container">
              <Select
                className={"ps-company-search__sort-by-select"}
                styles={customReactSelectStyles}
                value={selectedSortOption}
                onChange={(value) => {
                  handleCompaniesLoading();
                  handleClearPagingAndOffset();
                  setSelectedSortOption(value);
                }}
                options={sortByOptions}
                isSearchable={false}
              ></Select>
            </div>
          </div>
          <div>
            <div>
              {tagOptionsDisplayed === "market_tag" && (
                <React.Fragment>
                  <div className="ps-company-search__tags-grid-container">
                    {/* {marketTagsHtml} */}
                    {parentMarketTagsHtml}
                  </div>
                  <React.Fragment>
                    {childMarketTagOptionsToDisplay.length > 0 && (
                      <React.Fragment>
                        <div style={{ marginTop: "20px" }}>
                          {/* <hr className="ps-divider--primary" /> */}
                          <div className="ps-company-search__tags-grid-container">
                            {childMarketTagsHtml}
                          </div>
                        </div>
                      </React.Fragment>
                    )}
                  </React.Fragment>
                </React.Fragment>
              )}
              {tagOptionsDisplayed === "location_tag" && (
                <React.Fragment>
                  <div className="ps-company-search__tags-grid-container">
                    {countryTagsHtml}
                  </div>
                  <React.Fragment>
                    {stateTagOptionsToDisplay.length > 0 && (
                      <React.Fragment>
                        <div style={{ marginTop: "20px" }}>
                          {/* <hr className="ps-divider--primary" /> */}
                          <div className="ps-company-search__tags-grid-container">
                            {stateTagsHtml}
                          </div>
                        </div>
                        {cityTagOptionsToDisplay.length > 0 && (
                          <div style={{ marginTop: "20px" }}>
                            {/* <hr className="ps-divider--primary" /> */}
                            <div className="ps-company-search__tags-grid-container">
                              {cityTagsHtml}
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    )}
                  </React.Fragment>
                </React.Fragment>
              )}
              {tagOptionsDisplayed === "portfolio_tag" && (
                <div className="ps-company-search__tags-grid-container">
                  {classTagsHtml}
                </div>
              )}
              {tagOptionsDisplayed === "investment_round_tag" && (
                <div className="ps-company-search__tags-grid-container">
                  {investmentRoundTagsHtml}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {children()}
      {companiesResult && hasNextPage && !isFetchingNextPage && (
        <div className="ps-company-search__load-more-container">
          <button
            onClick={() => {
              fetchNextPage();
            }}
            className="btn ps-btn--white"
            disabled={!hasNextPage || isFetchingNextPage}
          >
            {isFetchingNextPage
              ? "Loading more..."
              : hasNextPage
              ? "Load More"
              : "Nothing more to load"}
          </button>
        </div>
      )}

      {/* {listToRender &&
        listToRender.pagination.limit > 0 &&
        listToRender.pagination.limit < listToRender.pagination.count && (
          <div className="ps-company-search__load-more-container">
            <button
              className="btn ps-btn--white"
              onClick={() => {
                let newOffset = offset + pageSize;

                setPreLoadedCompanies(listToRender.data);
                setOffset(newOffset);
              }}
            >
              Load More
            </button>
          </div>
        )} */}
    </div>
  );
};

export default HeadlessCompanySearch;
